import { useState, useEffect } from "react";
import {
  Plus, Mail, Phone, Building2, Search, Edit, Trash2, X, MapPin,
  ChevronDown, AlertTriangle, Users, LayoutGrid, List
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [userRole, setUserRole] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [viewMode, setViewMode] = useState("table");
  const [newContact, setNewContact] = useState({
    name: "", email: "", phone: "", company: "", city: "", tags: [], notes: "",
  });

  const { selectedBusiness, businessId } = useBusiness();

  useEffect(() => {
    fetchContacts();
    fetchUserRole();
  }, [businessId]);

  const fetchUserRole = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
      if (response.ok) {
        const user = await response.json();
        setUserRole(user.role);
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
    }
  };

  const fetchContacts = async (search = "") => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (businessId && userRole === "admin") {
        params.append("business_id", businessId);
      }
      const url = params.toString()
        ? `${BACKEND_URL}/api/contacts?${params.toString()}`
        : `${BACKEND_URL}/api/contacts`;
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => { fetchContacts(searchQuery); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // --- Role-based access ---
  const canEdit = userRole === "super_admin" || userRole === "admin";
  const canDelete = userRole === "super_admin" || userRole === "admin";

  // --- Selection helpers ---
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === contacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(contacts.map(c => c.contact_id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  // --- CRUD ---
  const handleCreateContact = async () => {
    if (!newContact.name.trim()) { toast.error("Please enter a contact name"); return; }
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(newContact),
      });
      if (response.ok) {
        toast.success("Contact created successfully!");
        setDialogOpen(false);
        setNewContact({ name: "", email: "", phone: "", company: "", city: "", tags: [], notes: "" });
        fetchContacts(searchQuery);
      }
    } catch { toast.error("Failed to create contact"); }
  };

  const handleEditContact = (contact) => {
    setEditingContact({
      contact_id: contact.contact_id,
      name: contact.name, email: contact.email || "",
      phone: contact.phone || "", company: contact.company || "",
      city: contact.city || "", notes: contact.notes || "",
    });
    setEditDialogOpen(true);
  };

  const handleUpdateContact = async () => {
    if (!editingContact.name.trim()) { toast.error("Please enter a contact name"); return; }
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts/${editingContact.contact_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: editingContact.name, email: editingContact.email || null,
          phone: editingContact.phone || null, company: editingContact.company || null,
          city: editingContact.city || null, notes: editingContact.notes || null,
        }),
      });
      if (response.ok) {
        toast.success("Contact updated!");
        setEditDialogOpen(false); setEditingContact(null);
        fetchContacts(searchQuery);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update contact");
      }
    } catch { toast.error("Failed to update contact"); }
  };

  const handleDeleteContact = async (contact) => {
    if (!window.confirm(`Delete "${contact.name}"? This cannot be undone.`)) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts/${contact.contact_id}`, {
        method: "DELETE", credentials: "include",
      });
      if (response.ok) {
        toast.success("Contact deleted!");
        setSelectedIds(prev => { const n = new Set(prev); n.delete(contact.contact_id); return n; });
        fetchContacts(searchQuery);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete contact");
      }
    } catch { toast.error("Failed to delete contact"); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts/bulk-delete`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.deleted} contact(s) deleted`);
        setSelectedIds(new Set());
        setDeleteConfirmOpen(false);
        fetchContacts(searchQuery);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Bulk delete failed");
      }
    } catch { toast.error("Bulk delete failed"); }
    finally { setBulkDeleting(false); }
  };

  const allSelected = contacts.length > 0 && selectedIds.size === contacts.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < contacts.length;

  return (
    <div className="space-y-6" data-testid="contacts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-heading font-bold text-slate-900 dark:text-white tracking-tight">
            Contacts
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm sm:text-base">Manage your business contacts</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-contact-button"
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 py-3 font-semibold shadow-lg shadow-indigo-500/30 transition-all hover:scale-105 active:scale-95 w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Contact
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
                Add New Contact
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input id="name" data-testid="contact-name-input" value={newContact.name}
                  onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                  className="bg-slate-50 border border-slate-200 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" data-testid="contact-email-input" value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  className="bg-slate-50 border border-slate-200 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" data-testid="contact-phone-input" value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  className="bg-slate-50 border border-slate-200 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="company">Company</Label>
                <Input id="company" data-testid="contact-company-input" value={newContact.company}
                  onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
                  className="bg-slate-50 border border-slate-200 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input id="city" data-testid="contact-city-input" placeholder="e.g., Mumbai, Delhi" value={newContact.city}
                  onChange={(e) => setNewContact({ ...newContact, city: e.target.value })}
                  className="bg-slate-50 border border-slate-200 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea id="notes" data-testid="contact-notes-input" value={newContact.notes}
                  onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                  className="bg-slate-50 border border-slate-200 rounded-xl" rows={3} />
              </div>
              <Button onClick={handleCreateContact} data-testid="create-contact-button"
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-semibold">
                Create Contact
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search + Bulk Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search by name, phone, or email..."
            data-testid="contact-search-input"
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 pr-10 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-base"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Bulk Actions */}
        {canDelete && selectedIds.size > 0 && (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-sm px-3 py-1.5">
              {selectedIds.size} selected
            </Badge>
            <Button variant="outline" size="sm" onClick={clearSelection}
              className="text-slate-500 rounded-lg text-xs">
              Clear
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="destructive" size="sm" className="rounded-lg gap-1">
                  Bulk Actions <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-700 focus:bg-red-50 gap-2"
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  <Trash2 className="w-4 h-4" /> Delete Selected ({selectedIds.size})
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* Count + View Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Users className="w-4 h-4" />
          {loading ? "Loading..." : `${contacts.length} contact${contacts.length !== 1 ? "s" : ""} found`}
        </div>
        <div className="flex items-center bg-slate-100 dark:bg-slate-700 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode("table")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              viewMode === "table"
                ? "bg-white dark:bg-slate-600 text-indigo-600 dark:text-indigo-400 shadow-sm"
                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
            }`}
          >
            <List className="w-3.5 h-3.5" /> Table
          </button>
          <button
            onClick={() => setViewMode("grid")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              viewMode === "grid"
                ? "bg-white dark:bg-slate-600 text-indigo-600 dark:text-indigo-400 shadow-sm"
                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Grid
          </button>
        </div>
      </div>

      {/* Table View */}
      {viewMode === "table" && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                  {canDelete && (
                    <th className="w-12 px-4 py-3 text-center">
                      <Checkbox
                        checked={allSelected}
                        data-state={someSelected ? "indeterminate" : allSelected ? "checked" : "unchecked"}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all"
                        className="data-[state=indeterminate]:bg-indigo-400"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300 uppercase text-xs tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300 uppercase text-xs tracking-wider hidden md:table-cell">Email</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300 uppercase text-xs tracking-wider hidden sm:table-cell">Phone</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300 uppercase text-xs tracking-wider hidden lg:table-cell">Company</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300 uppercase text-xs tracking-wider hidden lg:table-cell">City</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300 uppercase text-xs tracking-wider hidden xl:table-cell">Added</th>
                  {canEdit && (
                    <th className="px-4 py-3 text-center font-semibold text-slate-600 dark:text-slate-300 uppercase text-xs tracking-wider w-24">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {loading ? (
                  <tr>
                    <td colSpan={99} className="px-4 py-12 text-center text-slate-500">Loading contacts...</td>
                  </tr>
                ) : contacts.length === 0 ? (
                  <tr>
                    <td colSpan={99} className="px-4 py-12 text-center text-slate-500">
                      {searchQuery ? "No contacts match your search." : "No contacts yet. Add your first contact!"}
                    </td>
                  </tr>
                ) : (
                  contacts.map((contact) => {
                    const isSelected = selectedIds.has(contact.contact_id);
                    return (
                      <tr
                        key={contact.contact_id}
                        data-testid={`contact-row-${contact.contact_id}`}
                        className={`transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/30 ${isSelected ? "bg-indigo-50/50 dark:bg-indigo-900/10" : ""}`}
                      >
                        {canDelete && (
                          <td className="w-12 px-4 py-3 text-center">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleSelect(contact.contact_id)}
                              aria-label={`Select ${contact.name}`}
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-gradient-to-br from-indigo-600 to-violet-700 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0">
                              {(contact.name || "?").charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-slate-900 dark:text-white truncate">{contact.name}</p>
                              {contact.source && (
                                <Badge className="mt-0.5 bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0">
                                  {contact.source}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {contact.email ? (
                            <span className="text-slate-600 dark:text-slate-400 truncate block max-w-[200px]">{contact.email}</span>
                          ) : <span className="text-slate-300">-</span>}
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          {contact.phone ? (
                            <span className="text-slate-600 dark:text-slate-400">{contact.phone}</span>
                          ) : <span className="text-slate-300">-</span>}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          {contact.company ? (
                            <span className="text-slate-600 dark:text-slate-400 truncate block max-w-[150px]">{contact.company}</span>
                          ) : <span className="text-slate-300">-</span>}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          {contact.city ? (
                            <span className="text-slate-600 dark:text-slate-400">{contact.city}</span>
                          ) : <span className="text-slate-300">-</span>}
                        </td>
                        <td className="px-4 py-3 hidden xl:table-cell">
                          {contact.created_at ? (
                            <span className="text-slate-400 text-xs whitespace-nowrap">
                              {formatDistanceToNow(new Date(contact.created_at), { addSuffix: true })}
                            </span>
                          ) : <span className="text-slate-300">-</span>}
                        </td>
                        {canEdit && (
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="ghost" size="sm"
                                onClick={() => handleEditContact(contact)}
                                data-testid={`edit-contact-${contact.contact_id}`}
                                className="h-8 w-8 p-0 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg">
                                <Edit className="w-4 h-4" />
                              </Button>
                              {canDelete && (
                                <Button variant="ghost" size="sm"
                                  onClick={() => handleDeleteContact(contact)}
                                  data-testid={`delete-contact-${contact.contact_id}`}
                                  className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Grid View */}
      {viewMode === "grid" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <p className="col-span-3 text-center text-slate-600 py-12">Loading contacts...</p>
          ) : contacts.length === 0 ? (
            <Card className="col-span-3 bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700">
              <CardContent className="p-12 text-center">
                <p className="text-slate-600 dark:text-slate-400">
                  {searchQuery ? "No contacts match your search." : "No contacts yet. Add your first contact!"}
                </p>
              </CardContent>
            </Card>
          ) : (
            contacts.map((contact) => {
              const isSelected = selectedIds.has(contact.contact_id);
              return (
                <Card
                  key={contact.contact_id}
                  data-testid={`contact-card-${contact.contact_id}`}
                  className={`bg-white dark:bg-slate-800 rounded-2xl border shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all hover:-translate-y-1 duration-300 ${
                    isSelected ? "border-indigo-400 ring-1 ring-indigo-200" : "border-slate-100 dark:border-slate-700"
                  }`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {canDelete && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(contact.contact_id)}
                            aria-label={`Select ${contact.name}`}
                            className="mt-0.5"
                          />
                        )}
                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-violet-700 rounded-xl flex items-center justify-center text-white font-heading font-bold text-lg">
                          {(contact.name || "?").charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {canEdit && (
                          <Button variant="ghost" size="sm"
                            onClick={() => handleEditContact(contact)}
                            data-testid={`edit-contact-${contact.contact_id}`}
                            className="h-8 w-8 p-0 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50">
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button variant="ghost" size="sm"
                            onClick={() => handleDeleteContact(contact)}
                            data-testid={`delete-contact-${contact.contact_id}`}
                            className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-slate-900 dark:text-white mb-2">
                      {contact.name}
                    </h3>
                    <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                      {contact.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 shrink-0" />
                          <span className="truncate">{contact.email}</span>
                        </div>
                      )}
                      {contact.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 shrink-0" />
                          <span>{contact.phone}</span>
                        </div>
                      )}
                      {contact.company && (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 shrink-0" />
                          <span>{contact.company}</span>
                        </div>
                      )}
                      {contact.city && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 shrink-0" />
                          <span>{contact.city}</span>
                        </div>
                      )}
                    </div>
                    {contact.tags && contact.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {contact.tags.slice(0, 3).map((tag, idx) => (
                          <Badge key={`tag-${idx}`} className="bg-indigo-100 text-indigo-700 text-xs">{tag}</Badge>
                        ))}
                      </div>
                    )}
                    {contact.source && (
                      <Badge className="mt-2 bg-emerald-100 text-emerald-700 text-xs">
                        Source: {contact.source}
                      </Badge>
                    )}
                    {contact.notes && (
                      <p className="mt-3 text-xs text-slate-500 dark:text-slate-500 line-clamp-2">{contact.notes}</p>
                    )}
                    {contact.created_at && (
                      <p className="mt-3 text-xs text-slate-400">
                        Added {formatDistanceToNow(new Date(contact.created_at), { addSuffix: true })}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Edit Contact Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
              Edit Contact
            </DialogTitle>
          </DialogHeader>
          {editingContact && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Name *</Label>
                <Input id="edit-name" value={editingContact.name}
                  onChange={(e) => setEditingContact({ ...editingContact, name: e.target.value })}
                  className="bg-slate-50 border border-slate-200 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input id="edit-email" type="email" value={editingContact.email}
                  onChange={(e) => setEditingContact({ ...editingContact, email: e.target.value })}
                  className="bg-slate-50 border border-slate-200 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="edit-phone">Phone</Label>
                <Input id="edit-phone" value={editingContact.phone}
                  onChange={(e) => setEditingContact({ ...editingContact, phone: e.target.value })}
                  className="bg-slate-50 border border-slate-200 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="edit-company">Company</Label>
                <Input id="edit-company" value={editingContact.company}
                  onChange={(e) => setEditingContact({ ...editingContact, company: e.target.value })}
                  className="bg-slate-50 border border-slate-200 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="edit-city">City</Label>
                <Input id="edit-city" data-testid="edit-contact-city-input" placeholder="e.g., Mumbai, Delhi"
                  value={editingContact.city || ""}
                  onChange={(e) => setEditingContact({ ...editingContact, city: e.target.value })}
                  className="bg-slate-50 border border-slate-200 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="edit-notes">Notes</Label>
                <Textarea id="edit-notes" value={editingContact.notes}
                  onChange={(e) => setEditingContact({ ...editingContact, notes: e.target.value })}
                  className="bg-slate-50 border border-slate-200 rounded-xl" rows={3} />
              </div>
              <div className="flex gap-3">
                <Button onClick={handleUpdateContact}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-semibold">
                  Update Contact
                </Button>
                <Button onClick={() => { setEditDialogOpen(false); setEditingContact(null); }}
                  variant="outline" className="flex-1 rounded-full py-3">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Confirm Bulk Delete
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-600 dark:text-slate-400">
              Are you sure you want to delete <strong className="text-red-600">{selectedIds.size}</strong> contact(s)?
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button onClick={handleBulkDelete} disabled={bulkDeleting}
                variant="destructive" className="flex-1 rounded-full py-3 font-semibold">
                {bulkDeleting ? "Deleting..." : `Delete ${selectedIds.size} Contact(s)`}
              </Button>
              <Button onClick={() => setDeleteConfirmOpen(false)} variant="outline"
                className="flex-1 rounded-full py-3" disabled={bulkDeleting}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contacts;
